<template>
  <v-flex d-block xs12 >
    <v-card>
      <v-card-title>
        <h3 style="display: inline-block;">
          Cases
          <v-btn icon small @click="refreshData"><v-icon small>fa-sync</v-icon></v-btn>
          <export-cases-as-csv :filters="resultCaseFilters"/>
        </h3>
        <v-spacer/>
      </v-card-title>
      <v-container fluid :style="{'padding-top': 0}">
        <v-layout>
          <v-flex xs4 md-6 class="mt-4"><h4>Filters</h4></v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs4 md4 class="ml-3">
            <clients-select v-model="proxyClientIds"/>
          </v-flex>
          <v-flex xs4 md4 class="ml-3">
            <v-select
              :items="availableCaseStatuses"
              v-model="caseFilters.statuses"
              label="Please, select statuses"
              multiple
              item-value="value"
              item-text="title"
              persistent-hint
              clearable
              chips
              small-chips
              @change="fetchData"
            >
              <v-list-tile
                slot="prepend-item"
                ripple
                @click="handleSelectAllStatusesExceptArchived"
              >
                <v-list-tile-action>
                  <v-icon color="black">fas fa-check-double</v-icon>
                </v-list-tile-action>
                <v-list-tile-title>Select all except archived</v-list-tile-title>
              </v-list-tile>
              <v-divider
                slot="prepend-item"
                class="mt-2"
              ></v-divider>
            </v-select>
          </v-flex>
          <v-flex xs4 md4 class="ml-3">
            <v-autocomplete
              :items="filterServices || []"
              v-model="caseFilters.serviceIds"
              item-text="title"
              item-value="id"
              no-data-text="No services"
              label="Please, select services"
              multiple
              small-chips
              deletable-chips
              clearable
              @change="handleServicesFilterChanged"
            >
              <template
                slot="selection"
                slot-scope="data"
              >
                <v-chip
                  :color="data.item.backgroundColor"
                  close
                  class="chip--select-multi v-chip--small"
                  @input="removeServiceFromFilter(data.item)"
                >
                  <v-tooltip bottom>
                  <span slot="activator" v-bind:style="{'color': data.item.fontColor}">
                    {{ data.item.code }}
                  </span>
                    {{ data.item.title }}
                  </v-tooltip>
                </v-chip>
              </template>
            </v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs4 md4 class="ml-3">
            <v-text-field
              v-model="caseFilters.fulltextSearchString"
              label="Search by title / patient name / email"
              @input="debouncedHandleFulltextSearchStringInput"
              flat
            ></v-text-field>
          </v-flex>
          <v-flex xs4 md4 class="ml-3">
            <client-specialist-select v-model="proxyClientSpecialistIds"/>
          </v-flex>
          <v-flex xs4 md4 class="ml-3">
            Datetime range <span class="datetime-range-field">({{ currentDatetimeRangeField }})</span>:
            <v-menu full-width offset-y :close-on-content-click="false" v-model="filterCaseDateRangeMenuVisible" bottom>
              <v-btn color="primary" outline slot="activator">{{ caseFilters.dateFrom }} &mdash; {{ caseFilters.dateTo }}</v-btn>
              <v-card>
                <v-card-text>
                  <v-select
                    :items="caseFilterDatetimeOptions"
                    v-model="caseFilters.dateField"
                    label="Date field"
                    @input="handleDateFieldChange"
                  ></v-select>
                  <v-daterange :options="caseDateRangeFilterOptions"
                               @input="handleCaseFilterDateRangeChange"
                               no-presets/>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-flex>
        </v-layout>

        <v-layout>
          <v-flex xs12>
            <v-checkbox v-model="caseFilters.hideExcludedClients" :label="'Hide excluded clients from statistics'" v-on:change="handleAdditionalFiltersChange"></v-checkbox>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex xs4>
            <v-btn-toggle v-model="additionalFilters" @change="handleAdditionalFiltersChange">
              <v-btn flat value="onHold" large>
                On hold
              </v-btn>
              <v-btn flat value="reportNotSent" large>
                Report not sent
              </v-btn>
              <v-btn flat value="materialReturned" large>
                Material returned
              </v-btn>
              <v-btn flat value="materialNotReturned" large>
                Material not returned
              </v-btn>
              <v-btn flat value="lessThan24Hours" large>
                &lt; 24 hours
              </v-btn>
              <v-btn flat value="overdued" large>
                Overdued
              </v-btn>
              <v-btn flat value="services6xx" large>
                6XX
              </v-btn>
            </v-btn-toggle>
          </v-flex>
        </v-layout>
        <v-layout v-bind:style="{'display': 'inline-block', 'width': '100%'}">
            <v-data-table
              :headers="headers"
              :items="cases"
              :pagination.sync="pagination"
              :total-items="casesTotal"
              :loading="loading"
              :rows-per-page-items="[10, 25, 40, 50, 100]"
              class="elevation-1 cases-wide"
            >
              <template slot="headerCell" slot-scope="props">
                <v-tooltip bottom v-if="props.header.shortText">
                  <span slot="activator">
                    {{ props.header.shortText }}
                  </span>
                    {{ props.header.text }}
                </v-tooltip>
                <span v-else>{{ props.header.text }}</span>
              </template>

              <template slot="items" slot-scope="props">
                <tr @click="openEditCaseWindow(props.item)" :style="{'cursor': 'pointer'}">
                  <td v-if="!props.item.dpId">
                    {{ props.item.title | NoEmpty}}
                    <a :href="resolveCaseServicePageUrl(props.item.id)"
                       target="_blank"
                       class="caseServicesLink">
                      <v-icon small @click.stop=''>fa-file-invoice-dollar</v-icon>
                    </a>
                  </td>
                  <td v-else>
                    <a :href="fetchDPUrl + 'cases/' + props.item.dpId" target="_blank" @click.stop=''>{{ props.item.title}}</a>
                    <a :href="resolveCaseServicePageUrl(props.item.id)"
                       target="_blank"
                       class="caseServicesLink">
                      <v-icon small @click.stop=''>fa-file-invoice-dollar</v-icon>
                    </a>
                  </td>

                  <td>{{ props.item.creationDatetime | DateWithMinutes | NoEmpty}}</td>
                  <td>{{ props.item.receiptMaterialsDatetime | DateWithMinutes | NoEmpty}}</td>
                  <td v-if="props.item.status === 'validated' || props.item.status === 'archived'">
                    <span v-if="props.item.reportValidationEndedDate">{{ props.item.reportValidationEndedDate | DateWithMinutes | NoEmpty}}</span><span v-else>-</span>
                  </td>
                  <td v-else-if="props.item.estimatedCompletionDatetime && props.item.lisId != ''">
                    <div v-if="!props.item.onHold">
                      <countdown :date=props.item.estimatedCompletionDatetime :canBeNegative="true"></countdown>
                    </div>
                    <div v-else>
                      {{ secondsToHoursAndMinutes(props.item.secondsBeforeEstimatedCompletion) }}
                    </div>
                  </td>
                  <td v-else>-</td>
                  <td v-if="props.item.onHold" class="hold-status-text">On hold</td>
                  <td v-else>{{ getFormattedStatus(props.item.status) | NoEmpty }}</td>
                  <td>
                    <modal-case-service-log :case-id="props.item.id" :case-title="props.item.title" @refreshData="refreshData"/>
                    <restart-case-to-work-form :case-id="props.item.id"/>
                  </td>
                  <td>
                    <service-code-badges :servicesById="servicesById" :caseServices="props.item.services"/>
                  </td>
                  <td>
                    <span v-if="props.item.reportSentAt" class="report-sent-label"><i class="fas fa-envelope-square"></i> {{ formatReportSentAt(props.item.reportSentAt) }}</span><br/><span class="show-report" v-if="props.item.status === 'validated' || props.item.status === 'archived' && props.item.reportValidationEndedDate" @click.stop="handleShowReportClick(props.item)">Show report</span>
                    <span v-if="props.item.reportFiles && props.item.reportFiles.length > 0">
                      <span v-for="item in props.item.reportFiles" :key="item.fileId">
                        <span v-if="item.sentAt" class="report-sent-label"><i class="fas fa-envelope-square"></i> {{ formatReportSentAt(props.item.sentAt) }}</span>
                        <br/>
                        <span class="show-report" @click.stop="handleShowReportFileClick(props.item, item)">Show report {{item.fileName}}</span>
                        <br/>
                      </span>
                    </span>
                  </td>
                  <td>{{ props.item.patientName | NoEmpty}}</td>
                  <td>{{ getClientTitle(props.item.clientId) }}</td>
                  <td>{{ getClientSpecialistName(props.item) }}</td>
                  <td><text-tooltip-trimmer :value="props.item.comment" :length="100"/> </td>
                  <td>{{ props.item.price | currency}}</td>
                  <td>{{ props.item.paymentAmount | currency}}</td>
                </tr>
              </template>
            </v-data-table>
        </v-layout>
      </v-container>
      <v-layout row justify-center v-if="reportedCase">
        <v-dialog v-model="reportedCase" persistent lazy max-width="1000px" min-height="1100px" no-click-animation>
          <v-card>
            <v-toolbar dark color="primary">
              <v-btn icon dark @click="handleCloseCaseReport">
                <v-icon>close</v-icon>
              </v-btn>
              <v-toolbar-title>Case {{ reportedCase.title }}</v-toolbar-title>
              <v-spacer/>
            </v-toolbar>
            <v-card-text>
              <div>
                <div v-if="reportedCase.reportSentAt !== null">Report was sent at {{ reportedCase.reportSentAt | DateWithMinutes }}</div>
                <div v-if="reportedCase.reportFileSentAt !== null">Report file was sent at {{ reportedCase.reportFileSentAt | DateWithMinutes }}</div>
                <template v-if="!reportedCase.isReportFile">
                  <v-btn v-if="!reportedCase.reportSentAt" :loading="reportSending" :disabled="reportSending" @click="handleSendReportClick(reportedCase)">{{ getSendReportButtonTitle(reportedCase) }}</v-btn>
                  <v-dialog
                    v-else
                    v-model="resendDialog"
                    width="500"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :loading="reportSending"
                        :disabled="reportSending"
                        v-on=on
                      >
                        {{ getSendReportButtonTitle(reportedCase) }}
                      </v-btn>
                    </template>

                    <v-card>
                      <v-card-title
                        primary-title
                      >
                        Comment (will be sended by email as additional information)
                      </v-card-title>

                      <v-layout >
                        <v-flex xs12 mx-2>
                          <v-textarea
                            solo
                            v-model="resendComment"
                          ></v-textarea>
                        </v-flex>
                      </v-layout>

                      <v-divider></v-divider>

                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="primary"
                          flat
                          @click="handleResendReportClick(reportedCase, resendComment)"
                        >
                          send
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </template>
                <v-btn v-if="reportedCase.isReportFile" :loading="reportSending" :disabled="reportSending" @click="handleSendReportFileClick(reportedCase)">{{ getSendReportButtonTitle(reportedCase) }}</v-btn>
                <v-text-field
                  v-if="reportedCase.isReportFile"
                  v-model="reportedCase.reportFileComment"
                  label="Comment (will be sended by email as additional information)"
                  flat
                ></v-text-field>
                <v-menu offset-y>
                  <v-btn
                    slot="activator"
                    v-if="!reportedCase.isReportFile"
                  >
                    Download <v-icon class="fas fa-file-download" right dark></v-icon>
                  </v-btn>
                  <v-list>
                    <v-list-tile @click="handleClickReportDownloadButton(reportedCase, false)">
                      <v-list-tile-title>With signatures</v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="handleClickReportDownloadButton(reportedCase, true)">
                      <v-list-tile-title>Without signatures</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </div>

              <div class="report-wrapper text-xs-center">
                <v-progress-circular
                  :size="70"
                  :width="7"
                  style="margin-top: 230px"
                  color="primary"
                  indeterminate
                  v-if="reportLoading"
                ></v-progress-circular>
                <pdf v-show="!reportLoading" :src="reportSource" @loaded="handleReportLoaded" :page="currentReportPage" @num-pages="currentReportPagesCount = $event"></pdf>
                <div v-if="!reportLoading">
                  <v-pagination
                    v-model="currentReportPage"
                    :length="currentReportPagesCount"
                    prev-icon="fas fa-caret-left"
                    next-icon="fas fa-caret-right"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-layout>
      <v-layout row justify-center v-if="editedCase !== null">
        <v-dialog v-model="editCaseVisible" persistent lazy fullscreen no-click-animation>
          <case-details :servicesById="servicesById"
                        :clientsById="clientsById"
                        :availableClientServices="clientServicesByClientId[editedCase.clientId] || []"
                        :edited-case="editedCase"
                        @close="closeEditCaseWindow"
                        @removedServiceFromCase="handleRemoveServiceFromCase"
                        @addedServiceToCase="handleAddServiceFromCase"
                        @updatedCaseService="handleUpdateCaseService"
                        @caseFieldUpdated="handleCaseFieldUpdated"
                        @caseUpdated="handleCaseUpdated"
                        @requestedQuantityChanged="handleRequestedQuantityChanged"
                        @executedQuantityChanged="handleExecutedQuantityChanged"/>
        </v-dialog>
      </v-layout>
    </v-card>
  </v-flex>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import _ from 'lodash'
import axios from 'axios'
import FileSaver from 'file-saver'
import api from '../../api'
import {showApiError} from '../../helpers/notification.js'
import NoEmpty from '../Filters/NoEmpty.js'
import DateWithMinutes from '../Filters/DateWithMinutes.js'
import CaseDetails from './CaseDetails'
import SpecialistsInitials from '../SpecialistsInitials'
import ServiceCodeBadges from '../ServiceCodeBadges'
import TextTooltipTrimmer from '../Ui/TextTooltipTrimmer'
import Countdown from '../Countdown'
import {getContrastFontColorByBackground} from '../../helpers/styles'
import moment from 'moment'
import ClientsSelect from '../../components/Ui/mcc/ClientsSelect'
import ClientSpecialistSelect from '../../components/Ui/mcc/ClientSpecialistSelect'
import pdf from 'vue-pdf'
import ModalCaseServiceLog from "./case/ModalCaseServiceLog";
import {caseService, di} from '../../application'
import ExportCasesAsCsv from "./case/ExportCasesAsCsv";
import RestartCaseToWorkForm from "../case/RestartCaseToWorkForm";

const CreationDatetimeField = 'creation'
const ReportSentAtField = 'reportSent'
const MaterialReceivingAtField = 'materialReceiving'

export default {
  name: 'cases',
  components: {
    CaseDetails,
    SpecialistsInitials,
    ServiceCodeBadges,
    TextTooltipTrimmer,
    pdf,
    Countdown,
    ClientsSelect,
    ClientSpecialistSelect,
    ModalCaseServiceLog,
    ExportCasesAsCsv,
    RestartCaseToWorkForm
  },
  filters: {
    NoEmpty,
    DateWithMinutes
  },
  props: {
    services: {
      type: Array
    },
    servicesById: {
      type: Object
    },
    clientsById: {
      type: Object
    },
    cases: {
      type: Array
    },
    clients: {
      type: Array
    },
    clientServicesByClientId: {
      type: Object
    },
    casesTotal: {
      type: Number
    },
    availableCaseStatusesByValue: {
      type: Object
    },
    availableCaseStatuses: {
      type: Array
    }
  },
  data () {
    return {
      clientSearchInput: '',
      currentReportPage: 1,
      currentReportPagesCount: 0,
      reportSending: false,
      reportLoading: false,
      reportedCase: null,
      reportSource: null,
      caseDateRangeFilterOptions: {
        startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        format: 'YYYY-MM-DD'
      },
      filterCaseDateRangeMenuVisible: false,
      loading: true,
      editCaseVisible: false,
      pagination: {
        rowsPerPage: 40
      },
      caseFilters: {
        clientIds: [],
        specialistIds: [],
        serviceIds: [],
        dateFrom: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        dateTo: moment().format('YYYY-MM-DD'),
        dateField: CreationDatetimeField,
        fulltextSearchString: null,
        statuses: [],
        overdued: false,
        onHold: false,
        reportNotSent: false,
        materialReturned: false,
        materialNotReturned: false,
        lessThan24Hours: false,
        services6xx: false,
        hideExcludedClients: true
      },
      caseFilterDatetimeOptions: [
        {
          text: 'By case creation date',
          value: CreationDatetimeField
        },
        {
          text: 'By report sent date',
          value: ReportSentAtField
        },
        {
            text: 'By material receiving date',
            value: MaterialReceivingAtField
        }
      ],
      editedCase: null,
      headers: [
        {
          text: 'Title',
          align: 'left',
          sortable: false,
          value: 'name',
          width: '200px'
        },
        {
          text: 'Created date',
          value: 'creationDatetime',
          width: '150px'
        },
        {
          text: 'Materials receiving date',
          value: 'receiptMaterialsDatetime',
          width: '193px'
        },
        {
          text: 'Report validated at',
          value: 'reportValidatedAt',
          width: '186px'
        },
        {
          text: 'Status',
          value: 'status',
          width: '140px'
        },
        {
          text: 'Ops',
          value: 'Ops',
          width: '100px',
          sortable: false
        },
        {
          text: 'Services',
          value: 'services',
          width: '200px',
          sortable: false
        },
        {
          text: 'Report',
          value: 'report',
          width: '150px',
          sortable: false
        },
        {
          text: 'Patient name',
          value: 'patientName',
          width: '280px'
        },
        {
          text: 'Client',
          value: 'client',
          width: '250px'
        },
        {
          text: 'Client specialist',
          value: 'clientSpecialist',
          width: '250px'
        },
        {
          text: 'Comment',
          value: 'comment',
          width: '200px'
        },
        {
          text: 'Price',
          value: 'price',
          width: '150px'
        },
        {
          text: 'Paid amount',
          value: 'paymentAmount',
          width: '150px'
        }
      ],
      resendDialog: false,
      resendComment: "",
    }
  },
  computed: {
    ...mapGetters({
      availableServiceGroupsById: 'availableServiceGroupsById'
    }),
    currentDatetimeRangeField () {
      const index = _.findIndex(this.caseFilterDatetimeOptions, (o) => { return o.value === this.caseFilters.dateField })

      if (index < 0) {
        return ''
      }

      return this.caseFilterDatetimeOptions[index].text
    },
    resultCaseFilters () {
      let resultFilters = _.omit(this.caseFilters, ['dateFrom', 'dateTo', 'dateField'])

      let dateFromNormalized = moment(this.caseFilters.dateFrom).startOf('day').toISOString()
      let dateToNormalized = moment(this.caseFilters.dateTo).endOf('day').toISOString()

      if (this.caseFilters.dateField === CreationDatetimeField) {
        resultFilters.createdFrom = dateFromNormalized
        resultFilters.createdTo = dateToNormalized
      }

      if (this.caseFilters.dateField === ReportSentAtField) {
        resultFilters.reportSentFrom = dateFromNormalized
        resultFilters.reportSentTo = dateToNormalized
      }

      if (this.caseFilters.dateField === MaterialReceivingAtField) {
        resultFilters.materialReceivingFrom = dateFromNormalized
        resultFilters.materialReceivingTo = dateToNormalized
      }

      return resultFilters
    },
    proxyClientIds: {
      get() {
          return this.caseFilters.clientIds
      },
        set(newValue) {
            this.caseFilters.clientIds = newValue;
            this.fetchData()
        }
    },
    proxyClientSpecialistIds: {
        get() {
            return this.caseFilters.specialistIds
        },
        set(newValue) {
            this.caseFilters.specialistIds = newValue;
            this.fetchData()
        }
    },
    additionalFilters: {
      get () {
        if (this.caseFilters.overdued) {
          return 'overdued'
        }
        if (this.caseFilters.onHold) {
          return 'onHold'
        }
        if (this.caseFilters.reportNotSent) {
          return 'reportNotSent'
        }
        if (this.caseFilters.materialReturned) {
          return 'materialReturned'
        }
        if (this.caseFilters.materialNotReturned) {
          return 'materialNotReturned'
        }
        if (this.caseFilters.lessThan24Hours) {
          return 'lessThan24Hours'
        }
        if (this.caseFilters.services6xx) {
          return 'services6xx'
        }
      },
      set (value) {
        this.caseFilters.overdued = false
        this.caseFilters.onHold = false
        this.caseFilters.reportNotSent = false
        this.caseFilters.materialReturned = false
        this.caseFilters.materialNotReturned = false
        this.caseFilters.lessThan24Hours = false
        this.caseFilters.services6xx = false

        switch (value) {
          case 'overdued': {
            this.caseFilters.overdued = true
            break
          }
          case 'onHold': {
            this.caseFilters.onHold = true
            break
          }
          case 'reportNotSent': {
            this.caseFilters.reportNotSent = true
            break
          }
          case 'materialReturned': {
            this.caseFilters.materialReturned = true
            break
          }
          case 'materialNotReturned': {
            this.caseFilters.materialNotReturned = true
            break
          }
          case 'lessThan24Hours': {
            this.caseFilters.lessThan24Hours = true
            break
          }
          case 'services6xx': {
            this.caseFilters.services6xx = true
            break
          }
        }
      }
    },
    filterServices () {
      let result = []
      for (let service of this.services) {
        const backgroundColor = _.get(this.availableServiceGroupsById, [service.groupId, 'colorScheme'], void 0)
        result.push({
          id: service.id,
          code: service.code,
          title: `(${service.code}) ${service.title}`,
          backgroundColor: backgroundColor,
          fontColor: getContrastFontColorByBackground(backgroundColor)
        })
      }

      return _.orderBy(result, 'code', 'asc')
    },
    filterClients () {
      let result = []
      for (let client of this.clients) {
        result.push({
          id: client.id,
          title: client.title,
          shortTitle: _.truncate(client.title, {
            length: 15
          })
        })
      }

      return result
    },
    ...mapState({fetchDPUrl: state => state.dpURL})
  },
  watch: {
    pagination: {
      async handler () {
        this.getDataFromApi()
      },
      deep: true
    }
  },
  mounted () {
    this.getDataFromApi() // todo(TECH-391): !!! use store!
  },
  methods: {
    resolveCaseServicePageUrl(caseId) {
        let route = this.$router.resolve({name: 'CaseServicesPage', params: { caseId: caseId}});
        return route.href
    },
    secondsToHoursAndMinutes (seconds) {
      if (!seconds) {
        return '-'
      }

      let duration = moment.duration(seconds, 'seconds');
      return duration.format('HH:mm', {
        trim: false
      })
    },
    getFormattedStatus (statusValue) {
      return _.get(this.availableCaseStatusesByValue, [statusValue, 'title'], null)
    },
    handleReportLoaded () {
      this.reportLoading = false
    },
    async handleAdditionalFiltersChange () {
      await this.getDataFromApi()
      this.$set(this.pagination, 'page', 1)
    },
    async handleSendReportClick (c, resendComment) {
      this.reportSending = true
      try {
        let updatedCase = await api.caseSendReport(c.id, resendComment)
        this.reportSending = false

        this.handleCaseFieldUpdated({
          caseId: updatedCase.id,
          fieldName: 'reportSentAt',
          fieldValue: updatedCase.reportSentAt
        })
      } catch (e) {
        showApiError(this, e, 'Unable to send report')

        this.reportSending = false
      }
    },
    async handleResendReportClick(reportedCase, resendComment) {
      this.handleSendReportClick(reportedCase, resendComment);
      this.resendDialog = false;
      this.resendComment = "";
    },
    async handleSendReportFileClick (c) {
      this.reportSending = true
      try {
        let updatedCase = await api.caseSendReportFile(c.id, c.reportFileId, c.reportFilePath, c.reportFileComment)
        this.reportSending = false

        this.handleCaseFieldUpdated({
          caseId: updatedCase.id,
          fieldName: 'reportFiles',
          fieldValue: updatedCase.reportFiles
        })
      } catch (e) {
        showApiError(this, e, 'Unable to send report file')

        this.reportSending = false
      }
    },
    handleShowReportClick (c) {
      this.reportedCase = c
      this.reportLoading = true
      this.reportSource = {
        url: `/api/cases/get-report?caseId=${c.id}`,
        httpHeaders: {
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        }
      }
    },
    handleShowReportFileClick (c, f) {
      this.reportedCase = c

      this.reportedCase.isReportFile = true
      this.reportedCase.reportFileSentAt = f.sentAt
      this.reportedCase.reportFilePath = f.filePath
      this.reportedCase.reportFileId = f.fileId
      this.reportedCase.reportFileComment = f.fileComment

      this.reportLoading = true
      this.reportSource = {
        url: `/api/cases/get-report-file?filePath=${f.filePath}`,
        httpHeaders: {
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        }
      }
    },
    handleCloseCaseReport () {
      this.reportLoading = false
      this.reportedCase = null
      this.reportSource = null
      this.currentReportPagesCount = 0
      this.currentReportPage = 1
    },
    getSendReportButtonTitle (c) {
      if (c.reportSentAt) {
        return 'Resend report'
      }

      return 'Send report'
    },
    formatReportSentAt (reportSentAt) {
      return moment(reportSentAt).format('YYYY-MM-DD')
    },
    getClientTitle (clientId) {
      return _.get(this.clientsById, [clientId, 'title'], '-')
    },
    getClientSpecialistName (c) {
      let clientId = c.clientId
      let specialistId = c.clientSpecialistId

      let specialists = _.get(this.clientsById, [clientId, 'specialists'], null)

      if (specialists === null) {
        return '-'
      }

      let specialistIndex = _.findIndex(specialists, function (item) {
        return item.id === specialistId
      })

      if (specialistIndex < 0) {
        return '-'
      }

      return _.get(specialists, [specialistIndex, 'name'], '-')
    },
    getItemDataByPath (item, path, def = '-') {
      return _.get(item, path, def)
    },
    async getDataFromApi () {
      this.loading = true
      const { page, rowsPerPage } = this.pagination

      const resultFilters = this.resultCaseFilters

      try {
        let result = await caseService.getFilteredCases({
          skip: (page - 1) * rowsPerPage,
          limit: rowsPerPage,
          filters: resultFilters,
          sortBy: null,
          order: null,
        })
        this.$emit('casesReceived', {
          cases: result.cases,
          casesTotal: result.total
        })
        this.loading = false
      } catch (e) {
        showApiError(this, e, 'Unable to get cases')
      }
    },
    openEditCaseWindow (editedCase) {
      this.editedCase = editedCase
      this.editCaseVisible = true
    },
    closeEditCaseWindow() {
      this.editedCase = null
      this.editCaseVisible = false
    },
    handleRemoveServiceFromCase (result) {
      this.$emit('removedServiceFromCase', result)
    },
    handleAddServiceFromCase (addedItem) {
      this.$emit('addedServiceToCase', addedItem)
    },
    handleUpdateCaseService (updatedItem) {
      this.$emit('updatedCaseService', updatedItem)
    },
    handleCaseFieldUpdated (updatedItem) {
      this.$emit('caseFieldUpdated', updatedItem)
    },
    handleCaseUpdated (c) {
      this.editedCase = c
      this.$emit('caseUpdated', c)
    },
    async handleDateFieldChange () {
      await this.getDataFromApi()
      this.$set(this.pagination, 'page', 1)
    },
    async handleCaseFilterDateRangeChange (range) {
      this.caseFilters.dateFrom = range[0]
      this.caseFilters.dateTo = range[1]
      await this.getDataFromApi()
      this.$set(this.pagination, 'page', 1)
    },
    async fetchData() {
      await this.getDataFromApi();
      this.$set(this.pagination, 'page', 1);
    },
    async handleServicesFilterChanged () {
      await this.getDataFromApi()
      this.$set(this.pagination, 'page', 1)
    },
    async handleSpecialistsFilterChanged () {
      await this.getDataFromApi()
      this.$set(this.pagination, 'page', 1)
    },
    removeServiceFromFilter (item) {
      const index = this.caseFilters.serviceIds.indexOf(item.id)
      if (index >= 0) {
        this.caseFilters.serviceIds.splice(index, 1)
      }
    },
    async handleClickReportDownloadButton (c, noSignatures) {
      try {
        let reportData = await api.getCaseReport(c.id, noSignatures)
        let blob = new Blob([reportData], { type: 'application/pdf' })

        FileSaver.saveAs(blob, `${c.title}-report.pdf`)
      } catch (e) {
        showApiError(this, e, 'Unable to download report')
      }
    },
    handleSelectAllStatusesExceptArchived () {
      const resultStatuses = _.filter(this.availableCaseStatuses, (status) => {
        return status.value !== 'archived'
      })

      this.caseFilters = {
        ...this.caseFilters,
        statuses: _.map(resultStatuses, (status) => { return status.value })
      }
    },
    debouncedHandleFulltextSearchStringInput: _.debounce(async function (value) {
      if (value.length === 0) {
        this.caseFilters.fulltextSearchString = null
      }

      await this.getDataFromApi()
      this.$set(this.pagination, 'page', 1)
    }, 500),
    handleRequestedQuantityChanged(event) {
      this.$emit('requestedQuantityChanged', event)
    },
    handleExecutedQuantityChanged(event) {
      this.$emit('executedQuantityChanged', event)
    },
    refreshData() {
        console.log('refresh data')
        this.getDataFromApi()
    }
  }
}
</script>

<style scoped>
  .caseServicesLink {
    margin-left: 1em;
  }

</style>

<style>
  .cases-wide table {
    width: 2370px;
  }

  .show-report {
    text-decoration: underline;
    cursor: pointer;
  }

  .show-report:hover {
    color: #1976d2;
  }

  .report-wrapper {
    min-height: 500px;
    height: 500px;
    overflow-y: scroll;
  }

  .report-sent-label {
    color: gray;
    font-size: 11px;
  }

  .hold-status-text {
    color: #ffac26;
  }

  .datetime-range-field {
    font-size: 11px;
    opacity: .7;
  }
</style>



// WEBPACK FOOTER //
// src/components/Sales/Cases.vue